import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FiSearch, FiRefreshCw, FiUsers, FiChevronLeft, FiChevronRight,
  FiX, FiFilter, FiMail, FiCalendar, FiCreditCard, FiAlertCircle,
} from 'react-icons/fi';
import { getAdminOrganizations, AdminOrgRow } from '../../api/admin';

/* ── constantes ─────────────────────────────────────────────────────────────── */
const PLAN_BADGE: Record<string, string> = {
  free:       'bg-gray-100 text-gray-600',
  standard:   'bg-blue-100 text-blue-700',
  entreprise: 'bg-purple-100 text-purple-700',
  business:   'bg-orange-100 text-orange-700',
};
const STATUS_BADGE: Record<string, string> = {
  active:        'bg-green-100 text-green-700',
  trialing:      'bg-yellow-100 text-yellow-700',
  trial_expired: 'bg-red-100 text-red-600',
  canceled:      'bg-gray-100 text-gray-500',
  past_due:      'bg-orange-100 text-orange-700',
};
const STATUS_LABEL: Record<string, string> = {
  active:        'Actif',
  trialing:      'Essai',
  trial_expired: 'Essai expiré',
  canceled:      'Annulé',
  past_due:      'Paiement en retard',
};
const PLANS   = ['free', 'standard', 'entreprise', 'business'];
const STATUSES = ['active', 'trialing', 'trial_expired', 'canceled', 'past_due'];
const planLabel = (p: string | null) => p ? p.charAt(0).toUpperCase() + p.slice(1) : '—';

/* ── helpers ─────────────────────────────────────────────────────────────────── */
const fmtDate = (d: string) => new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });

function TrialBadge({ trialEnd }: { trialEnd: string | null }) {
  if (!trialEnd) return null;
  const days = Math.ceil((new Date(trialEnd).getTime() - Date.now()) / 86400000);
  if (days < 0) return <span className="text-xs text-red-500 flex items-center gap-0.5"><FiAlertCircle size={11} /> expiré</span>;
  return <span className="text-xs text-yellow-600">J-{days}</span>;
}

/* ── composant principal ─────────────────────────────────────────────────────── */
const AdminOrganizations: React.FC = () => {
  const [orgs, setOrgs]           = useState<AdminOrgRow[]>([]);
  const [total, setTotal]         = useState(0);
  const [page, setPage]           = useState(1);
  const [search, setSearch]       = useState('');
  const [inputVal, setInputVal]   = useState('');
  const [planFilter, setPlanFilter]     = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const limit = 20;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getAdminOrganizations({
        page, limit,
        search: search || undefined,
        ...(planFilter   ? { plan: planFilter }   : {}),
        ...(statusFilter ? { status: statusFilter } : {}),
      } as any);
      setOrgs(data.organizations);
      setTotal(data.total);
    } catch {
      setError('Impossible de charger les organisations');
    } finally {
      setLoading(false);
    }
  }, [page, search, planFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);

  /* Debounce recherche live */
  const handleInputChange = (val: string) => {
    setInputVal(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      setSearch(val);
    }, 400);
  };

  const clearSearch = () => {
    setInputVal('');
    setSearch('');
    setPage(1);
  };

  const totalPages = Math.ceil(total / limit);

  /* Statistiques rapides */
  const planCounts = orgs.reduce<Record<string, number>>((acc, o) => {
    acc[o.plan] = (acc[o.plan] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="p-6 max-w-7xl mx-auto">

      {/* ── Header ── */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Organisations</h2>
          <p className="text-sm text-gray-500 mt-0.5">{total} organisation{total !== 1 ? 's' : ''} au total</p>
        </div>
        <button
          onClick={load}
          className="flex items-center gap-2 px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition"
        >
          <FiRefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Actualiser
        </button>
      </div>

      {/* ── Stats rapides par plan (sur la page courante) ── */}
      {Object.keys(planCounts).length > 0 && (
        <div className="flex flex-wrap gap-2 mb-5">
          {PLANS.filter(p => planCounts[p]).map(p => (
            <button
              key={p}
              onClick={() => { setPlanFilter(planFilter === p ? '' : p); setPage(1); }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition
                ${planFilter === p
                  ? (PLAN_BADGE[p] || 'bg-gray-100 text-gray-600') + ' border-current ring-2 ring-offset-1 ring-current'
                  : (PLAN_BADGE[p] || 'bg-gray-100 text-gray-600') + ' border-transparent hover:opacity-80'
                }`}
            >
              {planLabel(p)}
              <span className="font-bold">{planCounts[p]}</span>
            </button>
          ))}
        </div>
      )}

      {/* ── Filtres & recherche ── */}
      <div className="flex flex-wrap gap-2 mb-4">
        {/* Recherche */}
        <div className="relative flex-1 min-w-[200px]">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15} />
          <input
            type="text"
            placeholder="Rechercher par nom, slug ou email..."
            value={inputVal}
            onChange={e => handleInputChange(e.target.value)}
            className="w-full pl-9 pr-8 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
          {inputVal && (
            <button onClick={clearSearch} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <FiX size={14} />
            </button>
          )}
        </div>

        {/* Filtre plan */}
        <div className="relative">
          <FiFilter className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={13} />
          <select
            value={planFilter}
            onChange={e => { setPlanFilter(e.target.value); setPage(1); }}
            className="pl-7 pr-8 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 focus:ring-2 focus:ring-indigo-500 appearance-none bg-white"
          >
            <option value="">Tous les plans</option>
            {PLANS.map(p => <option key={p} value={p}>{planLabel(p)}</option>)}
          </select>
        </div>

        {/* Filtre statut */}
        <div className="relative">
          <FiFilter className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={13} />
          <select
            value={statusFilter}
            onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
            className="pl-7 pr-8 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 focus:ring-2 focus:ring-indigo-500 appearance-none bg-white"
          >
            <option value="">Tous les statuts</option>
            {STATUSES.map(s => <option key={s} value={s}>{STATUS_LABEL[s] || s}</option>)}
          </select>
        </div>

        {/* Reset filtres */}
        {(planFilter || statusFilter || search) && (
          <button
            onClick={() => { setPlanFilter(''); setStatusFilter(''); clearSearch(); }}
            className="flex items-center gap-1.5 px-3 py-2 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition"
          >
            <FiX size={13} /> Réinitialiser
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
          <FiAlertCircle size={16} /> {error}
        </div>
      )}

      {/* ── Table ── */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Organisation</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Propriétaire</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Plan</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Statut</th>
                <th className="px-4 py-3 text-center font-semibold text-gray-600">
                  <span className="flex items-center justify-center gap-1"><FiUsers size={13} /> Membres</span>
                </th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Créée le</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-gray-400">
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                    Chargement…
                  </div>
                </td></tr>
              ) : orgs.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-gray-400">
                  Aucune organisation trouvée
                </td></tr>
              ) : orgs.map(org => (
                <React.Fragment key={org.id}>
                  <tr
                    className="hover:bg-gray-50 transition cursor-pointer"
                    onClick={() => setExpandedId(expandedId === org.id ? null : org.id)}
                  >
                    <td className="px-4 py-3">
                      <div className="font-semibold text-gray-900">{org.name}</div>
                      <div className="text-xs text-gray-400 font-mono">{org.slug}</div>
                    </td>
                    <td className="px-4 py-3">
                      {org.owner_email ? (
                        <div className="flex items-center gap-1.5 text-gray-600">
                          <FiMail size={13} className="text-gray-400 flex-shrink-0" />
                          <span className="truncate max-w-[180px]">{org.owner_email}</span>
                        </div>
                      ) : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${PLAN_BADGE[org.plan] || 'bg-gray-100 text-gray-600'}`}>
                          {planLabel(org.plan)}
                        </span>
                        {org.billing_cycle && (
                          <span className="text-xs text-gray-400">{org.billing_cycle === 'yearly' ? 'annuel' : 'mensuel'}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE[org.subscription_status] || 'bg-gray-100 text-gray-500'}`}>
                          {STATUS_LABEL[org.subscription_status] || org.subscription_status || '—'}
                        </span>
                        {org.subscription_status === 'trialing' && <TrialBadge trialEnd={org.trial_end} />}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="inline-flex items-center gap-1 font-medium text-gray-700">
                        <FiUsers size={13} className="text-gray-400" />
                        {org.user_count}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-500">
                      <div className="flex items-center gap-1.5">
                        <FiCalendar size={13} className="text-gray-300" />
                        {fmtDate(org.created_at)}
                      </div>
                    </td>
                  </tr>

                  {/* ── Ligne expandée ── */}
                  {expandedId === org.id && (
                    <tr className="bg-indigo-50/40">
                      <td colSpan={6} className="px-6 py-4">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-0.5">ID</p>
                            <p className="font-mono text-xs text-gray-700 break-all">{org.id}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-0.5">Facturation</p>
                            <p className="flex items-center gap-1.5 text-gray-700">
                              <FiCreditCard size={13} className="text-gray-400" />
                              {org.billing_cycle === 'yearly' ? 'Annuel' : org.billing_cycle === 'monthly' ? 'Mensuel' : '—'}
                            </p>
                          </div>
                          {org.trial_end && (
                            <div>
                              <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-0.5">Fin d'essai</p>
                              <p className="flex items-center gap-1.5 text-gray-700">
                                <FiCalendar size={13} className="text-gray-400" />
                                {fmtDate(org.trial_end)}
                              </p>
                            </div>
                          )}
                          <div>
                            <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-0.5">Propriétaire</p>
                            <p className="text-gray-700">{org.owner_email || '—'}</p>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        {/* ── Pagination ── */}
        {totalPages > 1 && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between bg-gray-50">
            <span className="text-xs text-gray-500">
              {((page - 1) * limit) + 1}–{Math.min(page * limit, total)} sur {total}
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage(1)} disabled={page === 1}
                className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-white disabled:opacity-40 transition"
              >«</button>
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                className="p-1.5 rounded border border-gray-300 hover:bg-white disabled:opacity-40 transition"
              ><FiChevronLeft size={14} /></button>
              <span className="px-3 py-1 text-xs text-gray-600">Page {page} / {totalPages}</span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                className="p-1.5 rounded border border-gray-300 hover:bg-white disabled:opacity-40 transition"
              ><FiChevronRight size={14} /></button>
              <button
                onClick={() => setPage(totalPages)} disabled={page === totalPages}
                className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-white disabled:opacity-40 transition"
              >»</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminOrganizations;
